import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Check, Loader2, Skull, Heart } from "lucide-react";

const ENERGIES = [
  { key: "charming_liar", label: "Charming liar" },
  { key: "loyal_sidekick", label: "Loyal sidekick" },
  { key: "chaos_agent", label: "Chaos agent" },
  { key: "mysterious_stranger", label: "Mysterious stranger" },
];

const Invite = () => {
  const { groupId } = useParams();
  const [group, setGroup] = useState(null);
  const [step, setStep] = useState(-1); // -1 pick-slot, 0..N questions, N+1 done
  const [slot, setSlot] = useState(null); // friend_token
  const [answers, setAnswers] = useState({
    name: "", secret: "", signature: "", energy: "", off_limits: "",
    email: "", phone: "",
    wedding_memory: "", wedding_wish: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    api.get(`/invite/${groupId}`).then((r) => setGroup(r.data)).catch(() => toast.error("Invite not found"));
  }, [groupId]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/invite/submit`, { friend_token: slot, ...answers });
      setSubmitted(true);
      setStep(group?.wedding_mode ? 9 : 7);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  if (!group) {
    return (
      <div className="grain min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-[#8b0000]" size={32} />
      </div>
    );
  }

  if (step === -1) {
    const filled = Object.keys(group.filled_tokens || {}).length;
    return (
      <div className="grain min-h-screen px-6 py-10 flex items-center" data-testid="invite-landing">
        <div className="max-w-xl mx-auto">
          <div className="font-hand text-[#d4af37] rotate-[-2deg] text-2xl mb-3">you've been summoned</div>
          <h1 className="font-display text-5xl sm:text-6xl leading-none mb-4">
            <span className="text-[#8b0000]">{group.host_name}</span> is throwing a murder.
          </h1>
          <p className="text-[#a39797] mb-10 text-lg">
            On {group.event_date}. You're a suspect. We just need to know who you are. Three questions,
            under 60 seconds. Everything you write is about you — nobody else.
          </p>
          <div className="card-noir p-6 mb-6">
            <div className="text-xs tracking-widest text-[#a39797] mb-3">PICK YOUR SLOT</div>
            <div className="grid grid-cols-2 gap-2">
              {group.friend_tokens.map((t, i) => (
                <button key={t} onClick={() => { setSlot(t); setStep(0); }}
                  data-testid={`slot-${i + 1}`}
                  className="btn-ghost text-sm">Suspect #{i + 1}</button>
              ))}
            </div>
            <div className="text-xs text-[#a39797] mt-4">
              {group.filled_count} of {group.size} friends have answered.
            </div>
          </div>
          <p className="font-hand text-[#b30000] rotate-[-1deg] text-xl">pick any — the host assigns roles later.</p>
        </div>
      </div>
    );
  }

  if (step === 7 || (group?.wedding_mode && step === 9)) {
    return (
      <div className="grain min-h-screen flex items-center justify-center px-6 py-16" data-testid="invite-done">
        <div className="text-center max-w-xl">
          <Check size={56} className="text-[#d4af37] mx-auto mb-6" />
          <div className="font-display text-4xl mb-3">You're in the case.</div>
          <p className="text-[#a39797] mb-8">
            {group.host_name} will finalize everything. You'll show up on the night, play your part, and try not to get framed.
          </p>
          <div className="font-hand text-[#b30000] rotate-[-1deg] text-2xl mb-10">see you at dinner.</div>

          <div className="card-noir p-6 relative overflow-hidden text-left">
            <div className="absolute inset-0 bg-gradient-to-br from-[#8b0000]/15 to-transparent pointer-events-none" />
            <div className="relative">
              <div className="font-hand text-[#d4af37] rotate-[-1deg] text-lg mb-1">your turn to host?</div>
              <h3 className="font-display text-2xl mb-2">Throw your own murder.</h3>
              <p className="text-[#a39797] text-sm mb-5">
                Had fun being a suspect? Host the next one. Pick a case, invite 6–12 friends, and let the whole group chat come to dinner.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/" className="btn-primary text-sm" data-testid="invite-host-own-cta">
                  <Skull size={16} /> Host your own
                </Link>
                <a href="https://gofund.me/70f3afe1f" target="_blank" rel="noopener noreferrer"
                  className="btn-ghost text-sm" data-testid="invite-support-cta">
                  <Heart size={14} /> Support the author
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const baseQuestions = [
    {
      key: "name",
      kicker: "first, your name",
      prompt: "What should we call you on the role card?",
      placeholder: "First name, nickname, whatever",
      wink: "This is the name your friends will see.",
    },
    {
      key: "secret",
      kicker: "a harmless 'secret'",
      prompt: "Something you'd be fine being 'accused' of at the table.",
      placeholder: "e.g. 'cries at every Pixar movie'",
      wink: "Keep it funny. The host will see it; your friends will too.",
      long: true,
    },
    {
      key: "signature",
      kicker: "your signature line",
      prompt: "A phrase or habit your friends tease you about.",
      placeholder: "e.g. 'actually, if we're being honest...'",
      wink: "We'll stitch this into one of your clue cards.",
      long: true,
    },
    {
      key: "energy",
      kicker: "your energy",
      prompt: "Which of these feels most like you tonight?",
      options: ENERGIES,
    },
    {
      key: "off_limits",
      kicker: "anything off-limits",
      prompt: "Topics the host should NOT joke about with you?",
      placeholder: "e.g. 'death jokes' — or 'nothing, go wild'",
      wink: "We'll check your card against this before export.",
      long: true,
      optional: true,
    },
    {
      key: "email",
      kicker: "where should we send your role?",
      prompt: "Your email — we'll send your private player link when the host locks the kit.",
      placeholder: "you@example.com",
      wink: "Optional. If you skip it, your host hands you the link in person.",
      optional: true,
    },
    {
      key: "phone",
      kicker: "want it by text too?",
      prompt: "Your phone (E.164 format, e.g. +14155551234) — we'll also text your private link.",
      placeholder: "+14155551234",
      wink: "Optional. US/Intl numbers OK.",
      optional: true,
    },
  ];

  const weddingQuestions = [
    {
      key: "wedding_memory",
      kicker: "the love story vault",
      prompt: "One memory, moment, or story about this couple you'd read out at the rehearsal.",
      placeholder: "e.g. 'the night they adopted the dog and both cried on FaceTime with me'",
      wink: "We'll weave this into the kit's Love Story Vault page.",
      long: true,
    },
    {
      key: "wedding_wish",
      kicker: "your prediction",
      prompt: "A wish or prediction for their wedding day tomorrow.",
      placeholder: "e.g. 'the dog eats a flower from the arch'",
      wink: "One of you may hear this read back as a 'secret' before the night is over.",
      long: true,
    },
  ];

  const questions = group?.wedding_mode ? [...baseQuestions, ...weddingQuestions] : baseQuestions;

  const q = questions[step];
  const value = answers[q.key] || "";
  const canNext = q.optional || (q.options ? !!value : value.trim().length > 0);

  const next = () => {
    if (step === questions.length - 1) submit();
    else setStep(step + 1);
  };

  return (
    <div className="grain min-h-screen flex items-center justify-center px-6 py-10" data-testid={`invite-step-${step}`}>
      <div className="max-w-xl w-full">
        <div className="text-xs text-[#a39797] tracking-widest mb-6">
          QUESTION {step + 1} OF {questions.length}
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <div className="font-hand text-[#d4af37] rotate-[-2deg] text-2xl mb-3">{q.kicker}</div>
            <h2 className="font-display text-3xl sm:text-5xl mb-6 leading-tight">{q.prompt}</h2>

            {q.options ? (
              <div className="grid grid-cols-2 gap-3">
                {q.options.map((o) => (
                  <button key={o.key}
                    onClick={() => setAnswers((a) => ({ ...a, energy: o.key }))}
                    data-testid={`energy-${o.key}`}
                    className={`card-noir p-5 text-left transition-colors ${
                      value === o.key ? "border-[#8b0000] bg-[#8b0000]/10" : "hover:border-[#8b0000]"
                    }`}>
                    <div className="font-display text-xl">{o.label}</div>
                  </button>
                ))}
              </div>
            ) : q.long ? (
              <textarea className="input-noir min-h-[120px] resize-none" placeholder={q.placeholder}
                value={value}
                onChange={(e) => setAnswers((a) => ({ ...a, [q.key]: e.target.value }))}
                data-testid={`answer-${q.key}`} />
            ) : (
              <input className="input-noir text-lg" placeholder={q.placeholder}
                value={value}
                onChange={(e) => setAnswers((a) => ({ ...a, [q.key]: e.target.value }))}
                data-testid={`answer-${q.key}`} />
            )}

            {q.wink && <div className="font-hand text-[#b30000] rotate-[-1deg] text-xl mt-4">{q.wink}</div>}

            <div className="mt-10 flex items-center justify-between">
              {step > 0 ? (
                <button onClick={() => setStep(step - 1)} className="btn-ghost text-sm" data-testid="back-step">
                  back
                </button>
              ) : <div />}
              <button onClick={next} disabled={!canNext || submitting} className="btn-primary disabled:opacity-50"
                data-testid="next-step">
                {submitting ? <Loader2 className="animate-spin" size={18} /> :
                  step === questions.length - 1 ? "Lock it in" : <>Next <ArrowRight size={18} /></>}
              </button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Invite;
